import React, { useContext, useState, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Tab,
  Grid,
  Stack,
  List,
  ListItem,
  CircularProgress,
  Chip,
  Divider,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  KeyboardDoubleArrowDown,
  KeyboardDoubleArrowUp,
} from "@mui/icons-material";
import { AppContext } from "../context";
import { pathJoinUrl } from "@smfa/utils/src";

// Common components
import LimitedTextField from "../components/common/LimitedTextField";
import FileUploadArea from "../components/common/FileUploadArea";
import FormActionBar from "../components/common/FormActionBar";
import SearchControls from "../components/common/SearchControls";
import DataTable from "../components/common/DataTable";
import TablePagination from "../components/common/TablePagination";

// API actions
import {
  addeditHomeInvolve,
  getHomeInvolvelist,
  addeditInvolve,
  getInvolvelist,
  getInvolveOrderlist,
  reorderGetInvolve,
  deleteInvolve,
  changeGetInvolveStatus,
} from "../actions/home.actions";
import ToolBody from "../components/common/ToolBody";
import { reorderList } from "../utils/reorderList";
import DeleteModal from "../ui-components/DeleteModal";
import IOSSwitch from "../components/common/IOSSwitch";

const ORDER_COLUMN_WIDTH = 56;
const BANNER_COLUMN_WIDTH = 180;
const ACTION_COLUMN_WIDTH = 104;

const Getinvolve = () => {
  const { storage } = useContext(AppContext);
  const [activeTab, setActiveTab] = useState("1");
  const [subtitleDialog, setSubtitleDialog] = useState({
    open: false,
    item: null,
  });
  const [descriptionDialog, setDescriptionDialog] = useState({
    open: false,
    item: null,
  });

  // INTRO SECTION STATE
  const introDefault = {
    home_get_involved_id: 0,
    involve_title: "",
    involve_title_error: false,
    involve_title_maxlenth: 50,
    involve_short_description: "",
    involve_short_description_maxlenth: 210,
    involve_short_description_error: false,
  };
  const [intro, setIntro] = useState(introDefault);

  // FORM SECTION STATE
  const formDefault = {
    involve_id: 0,
    involve_image: "",
    involve_image_name: "",
    involve_image_error: false,
    involve_image_mob: "",
    involve_image_mob_name: "",
    involve_image_mob_error: false,
    involve_title: "",
    involve_title_maxlenth: 50,
    involve_title_error: false,
    involve_short_description: "",
    involve_short_description_maxlenth: 120,
    involve_short_description_error: false,
    involve_description: "",
    involve_description_maxlenth: 270,
    involve_description_error: false,
    involve_sub_title: "",
    involve_sub_title_maxlenth: 70,
    involve_sub_title_error: false,
    involve_first: "",
    involve_first_maxlenth: 70,
    involve_first_error: false,
    involve_second: "",
    involve_second_maxlenth: 70,
    involve_second_error: false,
    involve_third: "",
    involve_third_maxlenth: 70,
    involve_third_error: false,
    involve_four: "",
    involve_four_maxlenth: 70,
    involve_four_error: false,
    involve_five: "",
    involve_five_maxlenth: 70,
    involve_five_error: false,
    involve_six: "",
    involve_six_maxlenth: 70,
    involve_six_error: false,
  };

  // UI state
  const [form, setForm] = useState(formDefault);
  const [deskImage, setDeskImage] = useState("");
  const [mobImage, setMobImage] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [deleteItem, setDeleteItem] = useState(null);

  // TABLE SECTION STATE
  const [tableData, setTableData] = useState({
    data: [],
    loading: true,
    pagination: {
      pageNo: 1,
      perPage: 50,
      totalCount: 0,
      totalPage: 1,
    },
    search: {
      text: "",
      type: "involve_title",
    },
    sort: {
      field: null,
      direction: null,
    },
  });
  const [reorderLoading, setReorderLoading] = useState(null);
  const [toggleLoading, setToggleLoading] = useState(null);

  // HANDLERS FOR INTRO SECTION
  const handleIntroChange = (event) => {
    const { name, value } = event.target;
    setSuccessMsg("");
    setErrorMsg("");

    setIntro((prev) => {
      const maxLength = prev[`${name}_maxlenth`];
      if (!maxLength || value.length <= maxLength) {
        return {
          ...prev,
          [name]: value,
          [`${name}_error`]: false,
        };
      }
      return prev;
    });
  };

  const submitIntro = () => {
    setLoading(true);
    setSuccessMsg("");
    setErrorMsg("");

    // Validate fields
    let hasError = false;
    const updatedIntro = { ...intro };

    if (intro.involve_title === "") {
      updatedIntro.involve_title_error = true;
      hasError = true;
    }

    if (intro.involve_short_description === "") {
      updatedIntro.involve_short_description_error = true;
      hasError = true;
    }

    if (hasError) {
      setIntro(updatedIntro);
      setLoading(false);
      return;
    }

    // Create form data
    const formData = new FormData();
    formData.append("home_get_involved_id", intro.home_get_involved_id);
    formData.append(
      "involve_short_description",
      intro.involve_short_description,
    );
    formData.append("involve_title", intro.involve_title);

    // Submit to API
    addeditHomeInvolve(formData)
      .then((res) => {
        if (res.data.status === true) {
          setSuccessMsg("GetInvolve Introduction Updated Successfully!");
        } else {
          setErrorMsg(
            res.data.message || "Something went wrong, please try again!",
          );
        }
      })
      .catch((error) => {
        setErrorMsg("Something went wrong, please try again!");
      })
      .finally(() => {
        setLoading(false);
        setTimeout(() => setSuccessMsg(""), 3000);
      });
  };

  // HANDLERS FOR FORM SECTION
  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setSuccessMsg("");
    setErrorMsg("");

    setForm((prev) => {
      const maxLength = prev[`${name}_maxlenth`];
      if (!maxLength || value.length <= maxLength) {
        return {
          ...prev,
          [name]: value,
          [`${name}_error`]: false,
        };
      }
      return prev;
    });
  };

  const handleDeskImageChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setDeskImage(URL.createObjectURL(file));
      setForm((prev) => ({
        ...prev,
        involve_image: file,
        involve_image_name: file.name,
        involve_image_error: false,
      }));
      setSuccessMsg("");
      setErrorMsg("");
    }
  };

  const handleMobImageChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setMobImage(URL.createObjectURL(file));
      setForm((prev) => ({
        ...prev,
        involve_image_mob: file,
        involve_image_mob_name: file.name,
        involve_image_mob_error: false,
      }));
      setSuccessMsg("");
      setErrorMsg("");
    }
  };

  const clearUpload = (field, clearPreview) => {
    setForm((prev) => ({
      ...prev,
      [field]: "",
      [`${field}_name`]: "",
    }));
    clearPreview("");
  };

  const submitForm = () => {
    setLoading(true);
    setSuccessMsg("");
    setErrorMsg("");

    // Validate required fields
    let hasError = false;
    const updatedForm = { ...form };

    const requiredFields = [
      "involve_title",
      "involve_short_description",
      "involve_description",
      "involve_sub_title",
      "involve_first",
      "involve_second",
      "involve_third",
      "involve_four",
      "involve_five",
      "involve_six",
    ];

    requiredFields.forEach((field) => {
      if (!updatedForm[field]) {
        updatedForm[`${field}_error`] = true;
        hasError = true;
      }
    });

    if (hasError) {
      setForm(updatedForm);
      setLoading(false);
      return;
    }

    // Create form data
    const formData = new FormData();
    formData.append("involve_id", form.involve_id);
    formData.append("involve_title", form.involve_title);
    formData.append(
      "involve_short_description",
      form.involve_short_description,
    );
    formData.append("involve_image", form.involve_image);
    formData.append("involve_image_mob", form.involve_image_mob);
    formData.append("involve_description", form.involve_description);
    formData.append("involve_sub_title", form.involve_sub_title);
    formData.append("involve_first", form.involve_first);
    formData.append("involve_second", form.involve_second);
    formData.append("involve_third", form.involve_third);
    formData.append("involve_four", form.involve_four);
    formData.append("involve_five", form.involve_five);
    formData.append("involve_six", form.involve_six);

    // Submit to API
    const isNew = form.involve_id === 0;

    addeditInvolve(formData)
      .then((res) => {
        if (res.data.status === true) {
          setSuccessMsg(
            isNew
              ? "GetInvolve Added Successfully!"
              : "GetInvolve Updated Successfully!",
          );

          // Reset form
          setForm(formDefault);
          setDeskImage("");
          setMobImage("");

          // Refresh table data
          fetchTableData();

          // Switch to list tab after successful save
          setTimeout(() => {
            setSuccessMsg("");
            setActiveTab("3");
          }, 1000);
        } else {
          setErrorMsg(
            res.data.message || "Something went wrong, please try again!",
          );
        }
      })
      .catch((error) => {
        setErrorMsg("Something went wrong, please try again!");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const resetForm = () => {
    setForm(formDefault);
    setDeskImage("");
    setMobImage("");
    setSuccessMsg("");
    setErrorMsg("");
  };

  // HANDLERS FOR TABLE SECTION
  const analyzeDataSort = (data, field, direction) => {
    // Extract the relevant field values for logging
    const values = data.map((item) => ({
      id: item.involve_id,
      title: item.involve_title,
      sortField: item[field],
    }));

    // Try manual sorting to see if data should change
    const sorted = [...data].sort((a, b) => {
      const aValue = a[field] || "";
      const bValue = b[field] || "";

      if (typeof aValue === "string") {
        return direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return direction === "asc" ? aValue - bValue : bValue - aValue;
      }
    });

    // Compare sorted values
    const sortedValues = sorted.map((item) => ({
      id: item.involve_id,
      title: item.involve_title,
      sortField: item[field],
    }));

    log.debug("Sorted would be:", sortedValues);
    log.debug(
      "Data would change:",
      JSON.stringify(values) !== JSON.stringify(sortedValues),
    );
    log.debug("------------------------------------------------------");

    return sorted;
  };

  const fetchTableData = (params = {}) => {
    setTableData((prev) => ({ ...prev, loading: true }));

    const requestParams = {
      pageNo: params.pageNo || tableData.pagination.pageNo,
      perPage: params.perPage || tableData.pagination.perPage,
      searchText:
        params.searchText !== undefined
          ? params.searchText
          : tableData.search.text,
      searchType: params.searchType || tableData.search.type,
    };

    // More robust sort parameter handling
    if (params.sort) {
      requestParams.sort = params.sort;
      log.debug(`Using provided sort direction: ${params.sort}`);
    } else if (tableData.sort.direction) {
      requestParams.sort = tableData.sort.direction;
      log.debug(`Using existing sort direction: ${tableData.sort.direction}`);
    }

    if (params.sortField) {
      requestParams.sortvalue = params.sortField;
      log.debug(`Using provided sort field: ${params.sortField}`);
    } else if (tableData.sort.field) {
      requestParams.sortvalue = tableData.sort.field;
      log.debug(`Using existing sort field: ${tableData.sort.field}`);
    }

    // More detailed logging
    log.debug("API request params:", JSON.stringify(requestParams, null, 2));

    // Choose API based on sort parameters
    let apiCall;
    if (requestParams.sort && requestParams.sortvalue) {
      log.debug("Using getInvolveOrderlist API for sorting");
      apiCall = getInvolveOrderlist(requestParams);
    } else {
      log.debug("Using standard getInvolvelist API");
      apiCall = getInvolvelist(requestParams);
    }

    // Execute API call
    apiCall
      .then((res) => {
        if (res?.data?.status !== true) {
          throw new Error(
            res?.data?.message || "Failed to load Getting Involved data.",
          );
        }

        // Keep malformed success payloads from reaching DataTable.
        let processedData = Array.isArray(res.data.data) ? res.data.data : [];

        // Analyze sort results when sort parameters are present
        if (requestParams.sort && requestParams.sortvalue) {
          const direction = requestParams.sort === "ASC" ? "asc" : "desc";
          const field = requestParams.sortvalue;

          // Analyze the received data to see if it's properly sorted
          // eslint-disable-next-line no-unused-vars
          const shouldBeSorted = analyzeDataSort(
            processedData,
            field,
            direction,
          );
          // UNCOMMENT THIS to force client-side sorting if server-side sort doesn't work
          // processedData = shouldBeSorted;
        }

        setTableData((prev) => ({
          ...prev,
          data: processedData,
          loading: false,
          pagination: {
            ...prev.pagination,
            pageNo: Number(requestParams.pageNo),
            perPage: Number(requestParams.perPage),
            totalCount: res.data.pagination?.totalCount || 0,
            totalPage: res.data.pagination?.totalPage || 1,
          },
        }));
      })
      .catch((error) => {
        setTableData((prev) => ({ ...prev, loading: false }));
        setErrorMsg("Failed to load data");
      });
  };

  const reorderInvolveItem = (involveId, direction) => {
    if (reorderLoading !== null) {
      return Promise.resolve(null);
    }

    const previousData = tableData.data;
    setReorderLoading(involveId);
    setTableData((prev) => ({
      ...prev,
      data: reorderList(prev.data, involveId, direction, "involve_id"),
    }));

    return reorderGetInvolve({
      involve_id: involveId,
      direction,
    })
      .then((res) => {
        if (res?.data?.status !== true) {
          throw new Error(
            res?.data?.message || "Getting Involved order could not be saved.",
          );
        }

        fetchTableData();
        return res;
      })
      .catch((error) => {
        log.error("Getting Involved reorder failed:", error);
        setTableData((prev) => ({ ...prev, data: previousData }));
        setErrorMsg(
          error?.response?.data?.message ||
            error?.message ||
            "Getting Involved order could not be saved.",
        );
        return null;
      })
      .finally(() => {
        setReorderLoading(null);
      });
  };

  const toggleInvolveStatus = (item) => {
    const nextStatus =
      item.involve_status === "active" ? "inactive" : "active";
    setToggleLoading(item.involve_id);

    return changeGetInvolveStatus({
      involve_id: item.involve_id,
      involve_status: nextStatus,
    })
      .then((res) => {
        if (res?.data?.status !== true) {
          throw new Error(
            res?.data?.message ||
              "Getting Involved status could not be saved.",
          );
        }

        const committedStatus =
          res.data.data?.involve_status || nextStatus;
        setTableData((prev) => ({
          ...prev,
          data: prev.data.map((currentItem) =>
            currentItem.involve_id === item.involve_id
              ? { ...currentItem, involve_status: committedStatus }
              : currentItem,
          ),
        }));
      })
      .catch((error) => {
        log.error("Getting Involved status update failed:", error);
        setErrorMsg(
          error?.response?.data?.message ||
            error?.message ||
            "Getting Involved status could not be saved.",
        );
      })
      .finally(() => {
        setToggleLoading(null);
      });
  };

  const handleSort = (field, direction) => {
    // Update local state
    setTableData((prev) => ({
      ...prev,
      sort: { field, direction },
      pagination: { ...prev.pagination, pageNo: 1 },
    }));

    // API expects specific parameter formats - make sure these match
    const sortDirection = direction === "asc" ? "asc" : "desc";

    // Call API with proper parameters
    fetchTableData({
      pageNo: 1,
      sort: sortDirection, // API might expect uppercase
      sortField: field,
    });
  };

  const handleSearch = () => {
    setTableData((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, pageNo: 1 },
    }));

    fetchTableData({ pageNo: 1 });
  };

  const handleSearchTextChange = (text) => {
    setTableData((prev) => ({
      ...prev,
      search: { ...prev.search, text },
    }));
  };

  const handleSearchTypeChange = (type) => {
    setTableData((prev) => ({
      ...prev,
      search: { ...prev.search, type },
    }));
  };

  const clearSearch = () => {
    setTableData((prev) => ({
      ...prev,
      search: { text: "", type: "involve_title" },
      pagination: { ...prev.pagination, pageNo: 1 },
    }));

    fetchTableData({
      pageNo: 1,
      searchText: "",
      searchType: "involve_title",
    });
  };

  const handlePageChange = (page) => {
    fetchTableData({ pageNo: page });
  };

  const handlePerPageChange = (perPage) => {
    fetchTableData({
      pageNo: 1,
      perPage,
    });
  };

  const handleEdit = (item) => {
    setForm({
      ...formDefault,
      involve_id: item.involve_id,
      involve_title: item.involve_title,
      involve_short_description: item.involve_short_description,
      involve_description: item.involve_description,
      involve_sub_title: item.involve_sub_title,
      involve_first: item.involve_first,
      involve_second: item.involve_second,
      involve_third: item.involve_third,
      involve_four: item.involve_four,
      involve_five: item.involve_five,
      involve_six: item.involve_six,
      involve_image_name: item.involve_image,
      involve_image_mob_name: item.involve_image_mob,
    });

    setDeskImage(
      item.involve_image
        ? pathJoinUrl(storage?.[0]?.imageEndUrl || "", item.involve_image)
        : "",
    );
    setMobImage(
      item.involve_image_mob
        ? pathJoinUrl(
            storage?.[0]?.imageEndUrl || "",
            item.involve_image_mob,
          )
        : "",
    );

    setSuccessMsg("");
    setErrorMsg("");
    setActiveTab("2");
  };

  const handleViewSubtitle = (item) => {
    setSubtitleDialog({
      open: true,
      item,
    });
  };

  const handleViewDescription = (item) => {
    setDescriptionDialog({
      open: true,
      item,
    });
  };

  const requestDelete = (item) => {
    setSuccessMsg("");
    setErrorMsg("");
    setDeleteItem(item);
  };

  const handleDeleteSuccess = () => {
    const deletedId = deleteItem?.involve_id;
    setDeleteItem(null);

    if (form.involve_id === deletedId) {
      resetForm();
      setActiveTab("2");
    }

    setSuccessMsg("Getting Involved section deleted successfully!");
    fetchTableData({ pageNo: 1 });
  };

  const handleDeleteError = (message) => {
    setErrorMsg(message || "Getting Involved section could not be deleted.");
  };

  // EFFECTS

  // Initialize data on component mount
  useEffect(() => {
    // Load intro section data
    getHomeInvolvelist().then((res) => {
      if (res.data.status === true) {
        setIntro({
          ...introDefault,
          home_get_involved_id: res.data.data.home_get_involved_id || 0,
          involve_title: res.data.data.involve_title || "",
          involve_short_description:
            res.data.data.involve_short_description || "",
        });
      }
    });

    // Load table data
    fetchTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // TABLE CONFIGURATION
  const tableColumns = [
    {
      field: "order",
      label: "Order",
      width: ORDER_COLUMN_WIDTH,
      minWidth: ORDER_COLUMN_WIDTH,
      maxWidth: ORDER_COLUMN_WIDTH,
      compact: true,
      cellSx: {
        verticalAlign: "top",
        whiteSpace: "nowrap",
      },
      render: (row, index) => {
        const firstIndex = 0;
        const lastIndex = tableData.data.length - 1;
        const canReorder =
          !tableData.search.text.trim() && !tableData.sort.field;
        const isFirst = canReorder && index === firstIndex;
        const isLast = canReorder && index === lastIndex;
        const rowLoading = reorderLoading === row.involve_id;
        const disabled =
          !canReorder || (reorderLoading !== null && !rowLoading);
        const buttons = [
          {
            direction: "first",
            label: "Move to first",
            icon: <KeyboardDoubleArrowUp fontSize="small" />,
            disabled: isFirst,
          },
          {
            direction: "up",
            label: "Move up",
            icon: <KeyboardArrowUp fontSize="small" />,
            disabled: isFirst,
          },
          {
            direction: "down",
            label: "Move down",
            icon: <KeyboardArrowDown fontSize="small" />,
            disabled: isLast,
          },
          {
            direction: "last",
            label: "Move to last",
            icon: <KeyboardDoubleArrowDown fontSize="small" />,
            disabled: isLast,
          },
        ];

        return rowLoading ? (
          <CircularProgress size={20} />
        ) : (
          <Stack spacing={0} sx={{ alignItems: "flex-start" }}>
            {buttons.map((button) => (
              <Tooltip title={button.label} key={button.direction}>
                <span>
                  <IconButton
                    size="small"
                    aria-label={button.label}
                    disabled={disabled || button.disabled}
                    onClick={() =>
                      reorderInvolveItem(row.involve_id, button.direction)
                    }
                    sx={{ p: 0.25 }}
                  >
                    {button.icon}
                  </IconButton>
                </span>
              </Tooltip>
            ))}
          </Stack>
        );
      },
    },
    {
      field: "involve_title",
      label: "Title/Subtitle",
      sortable: true,
      cellSx: {
        verticalAlign: "top",
      },
      render: (row) => (
        <Stack spacing={0.5} sx={{ alignItems: "flex-start" }}>
          <Box>{row.involve_title}</Box>
          <Divider
            flexItem
            sx={{
              borderColor: "text.secondary",
              borderBottomWidth: 2,
              width: "100%",
            }}
          />
          <Box>{row.involve_sub_title}</Box>
          <Chip
            clickable
            color="primary"
            icon={<OpenInNewIcon />}
            label="Subtitle Listing"
            size="small"
            onClick={() => handleViewSubtitle(row)}
          />
        </Stack>
      ),
    },
    {
      field: "involve_short_description",
      label: "Short Description",
      sortable: true,
      cellSx: {
        verticalAlign: "top",
      },
      render: (row) => (
        <Stack spacing={0.75} sx={{ alignItems: "flex-start" }}>
          <Box>{row.involve_short_description}</Box>
          <Divider
            flexItem
            sx={{
              borderColor: "text.secondary",
              borderBottomWidth: 2,
              width: "100%",
            }}
          />
          <Chip
            clickable
            color="primary"
            icon={<OpenInNewIcon />}
            label="Full Description"
            size="small"
            onClick={() => handleViewDescription(row)}
          />
        </Stack>
      ),
    },
    {
      field: "banners",
      label: "Banner",
      width: BANNER_COLUMN_WIDTH,
      minWidth: BANNER_COLUMN_WIDTH,
      maxWidth: BANNER_COLUMN_WIDTH,
      compact: true,
      cellSx: {
        verticalAlign: "top",
      },
      render: (row) =>
        row.involve_image && (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", fontWeight: 600 }}
            >
              Desktop
            </Typography>
            <Box sx={{ width: 100, height: 60 }}>
              <img
                src={pathJoinUrl(
                  storage?.[0]?.imageEndUrl || "",
                  row.involve_image,
                )}
                alt="Desktop"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>
          </Box>
        ),
    },
    {
      field: "actions",
      label: "Action",
      width: ACTION_COLUMN_WIDTH,
      minWidth: ACTION_COLUMN_WIDTH,
      maxWidth: ACTION_COLUMN_WIDTH,
      align: "right",
      compact: true,
      sticky: "right",
      cellSx: {
        verticalAlign: "top",
        whiteSpace: "nowrap",
        textAlign: "right",
      },
      render: (row) => (
        <Stack spacing={0.5} sx={{ alignItems: "flex-end" }}>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Edit Getting Involved section">
              <IconButton
                color="primary"
                aria-label="Edit Getting Involved section"
                size="small"
                onClick={() => handleEdit(row)}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Getting Involved section">
              <IconButton
                color="error"
                aria-label="Delete Getting Involved section"
                size="small"
                onClick={() => requestDelete(row)}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          {toggleLoading === row.involve_id ? (
            <CircularProgress size={24} />
          ) : (
            <Tooltip title="Enable or disable this Getting Involved section on the public page">
              <FormControlLabel
                control={
                  <IOSSwitch
                    checked={row.involve_status === "active"}
                    onChange={() => toggleInvolveStatus(row)}
                    slotProps={{
                      input: {
                        "aria-label":
                          "Enable or disable Getting Involved section",
                      },
                    }}
                  />
                }
                label=""
              />
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  const searchOptions = [
    { value: "involve_title", label: "Title" },
    { value: "involve_short_description", label: "Short Description" },
  ];

  return (
    <ToolBody>
      <TabContext value={activeTab}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabList
            onChange={(e, newValue) => {
              setActiveTab(newValue);
              setSuccessMsg("");
              setErrorMsg("");

              if (newValue === "2") {
                // If going to add/edit tab, reset form if not editing
                if (form.involve_id === 0) {
                  resetForm();
                }
              }
            }}
          >
            <Tab label="Getting Involved Introduction section" value="1" />
            <Tab
              label={`${form.involve_id !== 0 ? "Update" : "Add"} Getting Involved`}
              value="2"
            />
            <Tab label="Manage Getting Involved" value="3" />
          </TabList>
        </Box>

        {/* TAB 1: INTRODUCTION SECTION */}
        <TabPanel value="1">
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <LimitedTextField
                name="involve_title"
                label="Title"
                value={intro.involve_title}
                onChange={handleIntroChange}
                maxLength={intro.involve_title_maxlenth}
                error={intro.involve_title_error}
                errorText="Please enter Title!"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 8 }}>
              <LimitedTextField
                name="involve_short_description"
                label="Short Description"
                value={intro.involve_short_description}
                onChange={handleIntroChange}
                maxLength={intro.involve_short_description_maxlenth}
                error={intro.involve_short_description_error}
                errorText="Please enter Short Description!"
                multiline
                rows={2}
              />
            </Grid>
          </Grid>

          <FormActionBar
            onSubmit={submitIntro}
            isSubmitting={loading}
            isEdit={intro.home_get_involved_id !== 0}
            successMessage={successMsg}
            errorMessage={errorMsg}
            submitLabel="Update"
          />
        </TabPanel>

        {/* TAB 2: ADD/EDIT FORM */}
        <TabPanel value="2">
          <Stack spacing={3}>
            {/* Title and Short Description */}
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <LimitedTextField
                  name="involve_title"
                  label="Title"
                  value={form.involve_title}
                  onChange={handleFormChange}
                  maxLength={form.involve_title_maxlenth}
                  error={form.involve_title_error}
                  errorText="Please enter Title!"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 8 }}>
                <LimitedTextField
                  name="involve_short_description"
                  label="Short Description"
                  value={form.involve_short_description}
                  onChange={handleFormChange}
                  maxLength={form.involve_short_description_maxlenth}
                  error={form.involve_short_description_error}
                  errorText="Please enter Short Description!"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>

            {/* Long Description */}
            <LimitedTextField
              name="involve_description"
              label="Long Description"
              value={form.involve_description}
              onChange={handleFormChange}
              maxLength={form.involve_description_maxlenth}
              error={form.involve_description_error}
              errorText="Please enter Long Description!"
              multiline
              rows={3}
            />

            {/* Subtitle */}
            <LimitedTextField
              name="involve_sub_title"
              label="Subtitle"
              value={form.involve_sub_title}
              onChange={handleFormChange}
              maxLength={form.involve_sub_title_maxlenth}
              error={form.involve_sub_title_error}
              errorText="Please enter Subtitle!"
            />

            {/* Subtitle Listing Section */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Subtitle Listing
              </Typography>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <LimitedTextField
                    name="involve_first"
                    label="Point One"
                    value={form.involve_first}
                    onChange={handleFormChange}
                    maxLength={form.involve_first_maxlenth}
                    error={form.involve_first_error}
                    errorText="Please enter Point One!"
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <LimitedTextField
                    name="involve_second"
                    label="Point Two"
                    value={form.involve_second}
                    onChange={handleFormChange}
                    maxLength={form.involve_second_maxlenth}
                    error={form.involve_second_error}
                    errorText="Please enter Point Two!"
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <LimitedTextField
                    name="involve_third"
                    label="Point Three"
                    value={form.involve_third}
                    onChange={handleFormChange}
                    maxLength={form.involve_third_maxlenth}
                    error={form.involve_third_error}
                    errorText="Please enter Point Three!"
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <LimitedTextField
                    name="involve_four"
                    label="Point Four"
                    value={form.involve_four}
                    onChange={handleFormChange}
                    maxLength={form.involve_four_maxlenth}
                    error={form.involve_four_error}
                    errorText="Please enter Point Four!"
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <LimitedTextField
                    name="involve_five"
                    label="Point Five"
                    value={form.involve_five}
                    onChange={handleFormChange}
                    maxLength={form.involve_five_maxlenth}
                    error={form.involve_five_error}
                    errorText="Please enter Point Five!"
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <LimitedTextField
                    name="involve_six"
                    label="Point Six"
                    value={form.involve_six}
                    onChange={handleFormChange}
                    maxLength={form.involve_six_maxlenth}
                    error={form.involve_six_error}
                    errorText="Please enter Point Six!"
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Image Upload Section */}
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FileUploadArea
                  title="Upload Image"
                  description={
                    <>
                      Best image Resolution: 416px X 357px
                      <br />
                      Supported files types: JPG, JPEG & PNG
                    </>
                  }
                  previewUrl={deskImage}
                  fileName={form.involve_image_name}
                  onChange={handleDeskImageChange}
                  deleteRequest={{
                    resource: "get-involve",
                    id: form.involve_id,
                    field: "involve_image",
                  }}
                  onRemove={() => clearUpload("involve_image", setDeskImage)}
                  accept=".png,.jpg,.jpeg"
                  error={form.involve_image_error}
                  errorText="Please upload an image!"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <FileUploadArea
                  title="Upload Image For Details Page"
                  description={
                    <>
                      Best image Resolution: 416px X 357px
                      <br />
                      Supported files types: JPG, JPEG & PNG
                    </>
                  }
                  previewUrl={mobImage}
                  fileName={form.involve_image_mob_name}
                  onChange={handleMobImageChange}
                  deleteRequest={{
                    resource: "get-involve",
                    id: form.involve_id,
                    field: "involve_image_mob",
                  }}
                  onRemove={() =>
                    clearUpload("involve_image_mob", setMobImage)
                  }
                  accept=".png,.jpg,.jpeg"
                  error={form.involve_image_mob_error}
                  errorText="Please upload an image!"
                />
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <FormActionBar
              onSubmit={submitForm}
              onReset={resetForm}
              onDelete={
                form.involve_id !== 0 ? () => requestDelete(form) : undefined
              }
              isSubmitting={loading}
              isEdit={form.involve_id !== 0}
              successMessage={successMsg}
              errorMessage={errorMsg}
              submitLabel={form.involve_id !== 0 ? "Update" : "Save"}
              resetLabel="Reset"
            />
          </Stack>
        </TabPanel>

        {/* TAB 3: MANAGE LIST */}
        <TabPanel value="3">
          {/* Search Controls */}
          <SearchControls
            searchText={tableData.search.text}
            searchType={tableData.search.type}
            searchOptions={searchOptions}
            onSearchChange={handleSearchTextChange}
            onTypeChange={handleSearchTypeChange}
            onSubmit={handleSearch}
            onClear={clearSearch}
            perPage={tableData.pagination.perPage}
            totalCount={tableData.pagination.totalCount}
            onPerPageChange={handlePerPageChange}
          />

          {/* Data Table */}
          <DataTable
            columns={tableColumns}
            data={tableData.data}
            loading={tableData.loading}
            sortConfig={tableData.sort}
            onSort={handleSort}
            keyField="involve_id"
            tableLayout="fixed"
          />

          {/* Pagination */}
          {tableData.pagination.totalPage > 1 && (
            <TablePagination
              page={tableData.pagination.pageNo}
              totalPages={tableData.pagination.totalPage}
              loading={tableData.loading}
              onChange={handlePageChange}
              entriesPerPage={tableData.pagination.perPage}
              onEntriesChange={handlePerPageChange}
            />
          )}
        </TabPanel>
      </TabContext>

      {/* SUBTITLE DETAILS DIALOG */}
      <Dialog
        open={subtitleDialog.open}
        onClose={() => setSubtitleDialog({ open: false, item: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Subtitle Listing
          <IconButton
            aria-label="close"
            onClick={() => setSubtitleDialog({ open: false, item: null })}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {subtitleDialog.item && (
            <List>
              <ListItem>{subtitleDialog.item.involve_first}</ListItem>
              <ListItem>{subtitleDialog.item.involve_second}</ListItem>
              <ListItem>{subtitleDialog.item.involve_third}</ListItem>
              <ListItem>{subtitleDialog.item.involve_four}</ListItem>
              <ListItem>{subtitleDialog.item.involve_five}</ListItem>
              <ListItem>{subtitleDialog.item.involve_six}</ListItem>
            </List>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={descriptionDialog.open}
        onClose={() => setDescriptionDialog({ open: false, item: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Full Description
          <IconButton
            aria-label="close"
            onClick={() =>
              setDescriptionDialog({ open: false, item: null })
            }
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ whiteSpace: "pre-wrap" }}>
            {descriptionDialog.item?.involve_description ||
              "No full description available."}
          </Typography>
        </DialogContent>
      </Dialog>

      <DeleteModal
        open={deleteItem !== null}
        title="Delete Getting Involved section"
        message="Are you sure you want to delete this Getting Involved section?"
        handleClose={() => setDeleteItem(null)}
        handleSuccess={handleDeleteSuccess}
        handleError={handleDeleteError}
        deleteAction={deleteInvolve}
        data={deleteItem ? { involve_id: deleteItem.involve_id } : null}
      />
    </ToolBody>
  );
};

export default Getinvolve;
